// pages/ResetPasswordPage.jsx
import React, { useState, useMemo, useEffect } from 'react';
import API from '../api';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import '../styles/global.css';

export default function ResetPasswordPage() {
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [touched, setTouched] = useState({});
  const [timer, setTimer] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  useEffect(() => { setMounted(true); }, []);

  // Redirect if no email
  useEffect(() => {
    if (!email) navigate('/forgot-password');
  }, [email, navigate]);

  // Timer
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const passwordValid = useMemo(() => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/.test(password), [password]);
  const confirmMatches = password === confirmPassword && confirmPassword.length > 0;
  const otpValid = /^\d{6}$/.test(otp);
  const formValid = otpValid && passwordValid && confirmMatches;

  const touch = (field) => setTouched((t) => ({ ...t, [field]: true }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setTouched({ otp: true, password: true, confirmPassword: true });

    if (!formValid) return;

    setLoading(true);

    try {
      await API.post('/users/reset-password', { email, otp, newPassword: password });
      setSuccess('Password reset successful! Redirecting...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Password reset failed.');
      setTimeout(() => setError(''), 5000);
    }
    setLoading(false);
  };

  const handleResendOTP = async () => {
    if (timer > 0) return;
    setResendLoading(true);
    setError('');
    try {
      await API.post('/users/resend-reset-otp', { email });
      setSuccess('OTP Resent!');
      setTimer(60);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP');
    }
    setResendLoading(false);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        .page-container {
          min-height: 100vh;
          display: flex;
          font-family: 'Poppins', sans-serif;
          background-color: #1a2b3c;
          background-image: radial-gradient(circle at 10% 20%, rgba(4, 159, 137, 0.1) 0%, transparent 20%),
                            radial-gradient(circle at 90% 80%, rgba(4, 159, 137, 0.1) 0%, transparent 20%);
          overflow: hidden;
          position: relative;
        }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        @keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        
        .form-input::placeholder { color: rgba(255,255,255,0.4); }
        @media (max-width: 900px) {
          .left-panel { display: none !important; }
          .right-panel { width: 100% !important; padding: 20px !important; }
        }
      `}</style>

      <div className="page-container">
        {/* LEFT PANEL */}
        <div className="left-panel" style={styles.leftPanel}>
          <div style={styles.illustrationContainer}>
            <img 
              src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Locked.png" 
              alt="Reset Password 3D" 
              style={styles.heroImage}
            />
            <div style={styles.heroTextContainer}>
              <h1 style={styles.heroTitle}>Secure Your Account</h1>
              <p style={styles.heroSubtitle}>
                Create a strong password to protect your pharmacy data.<br/>
                We recommend using a mix of letters, numbers, and symbols.
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="right-panel" style={styles.rightPanel}>
          <div style={{
            ...styles.formCard,
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
          }}>
            <div style={styles.headerSection}>
              <div style={styles.iconCircle}>🔑</div>
              <h2 style={styles.headerTitle}>Reset Password</h2>
              <p style={styles.headerSubtitle}>Code sent to <strong>{email}</strong></p>
            </div>

            {success && <div style={styles.successBox}><span>✓</span> {success}</div>}
            {error && <div style={styles.errorBox}><span>⚠</span> {error}</div>}

            <form onSubmit={handleSubmit}>
              {/* OTP */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>OTP Code</label>
                <div style={styles.inputWrapper(focusedField === 'otp', touched.otp && !otpValid)}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setError('');
                    }}
                    onFocus={() => setFocusedField('otp')}
                    onBlur={() => { setFocusedField(''); touch('otp'); }}
                    style={{...styles.input, letterSpacing: '4px', fontWeight: 'bold'}}
                    maxLength={6}
                  />
                </div>
                {touched.otp && !otpValid && <div style={styles.fieldError}>• Enter valid 6-digit OTP</div>}
                
                <div style={{ textAlign: 'right', marginTop: '8px' }}>
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={timer > 0 || resendLoading}
                    style={styles.resendBtn(timer > 0)}
                  >
                    {resendLoading ? 'Sending...' : timer > 0 ? `Resend in ${timer}s` : 'Resend OTP'}
                  </button>
                </div>
              </div>

              {/* Password */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>New Password</label>
                <div style={styles.inputWrapper(focusedField === 'password', touched.password && !passwordValid)}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder="New Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => { setFocusedField(''); touch('password'); }}
                    style={styles.input}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={styles.eyeBtn}>
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                <PasswordStrengthIndicator password={password} />
              </div>

              {/* Confirm Password */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>Confirm Password</label>
                <div style={styles.inputWrapper(focusedField === 'confirm', touched.confirmPassword && !confirmMatches)}>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setFocusedField('confirm')}
                    onBlur={() => { setFocusedField(''); touch('confirmPassword'); }}
                    style={styles.input}
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} style={styles.eyeBtn}>
                    {showConfirm ? '🙈' : '👁️'}
                  </button>
                </div>
                {touched.confirmPassword && !confirmMatches && <div style={styles.fieldError}>• Passwords do not match</div>}
              </div>

              <button 
                type="submit" 
                disabled={loading || !formValid} 
                style={styles.submitBtn(loading || !formValid)}
              >
                {loading ? 'RESETTING...' : 'RESET PASSWORD'}
              </button>

              <div style={styles.footerLink}>
                <Link to="/login" style={{ color: '#81e6d9', fontWeight: 600, textDecoration:'none' }}>
                  ← Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

// Indicator Component
function PasswordStrengthIndicator({ password }) {
  if (!password) return null;
  const strength = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[a-z]/.test(password),
    /\d/.test(password),
    /[^\w\s]/.test(password)
  ].filter(Boolean).length;
  
  const colors = ['#e53e3e', '#dd6b20', '#d69e2e', '#38a169', '#38a169'];
  const labels = ['Weak', 'Fair', 'Good', 'Strong', 'Excellent'];

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{ width: `${(strength / 5) * 100}%`, height: '100%', background: colors[strength - 1] || '#e53e3e', transition: 'all 0.3s' }} />
      </div>
      <div style={{ fontSize: '11px', marginTop: '4px', color: colors[strength - 1] || '#e53e3e', fontWeight: '500' }}>
        {labels[strength - 1]}
      </div>
    </div>
  );
}

// Reuse styles
const styles = {
  leftPanel: { width: '55%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 },
  rightPanel: { width: '25%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2 },
  illustrationContainer: { width: '100%', maxWidth: '600px', padding: '40px', textAlign: 'center' },
  heroImage: { width: '280px', height: 'auto', animation: 'float 6s ease-in-out infinite', marginBottom: '30px', filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.5))' },
  heroTextContainer: { animation: 'slideUp 0.8s ease-out' },
  heroTitle: { color: '#fff', fontSize: '36px', fontWeight: '700', marginBottom: '15px', lineHeight: 1.2 },
  heroSubtitle: { color: '#a0aec0', fontSize: '16px', lineHeight: 1.6, maxWidth: '400px', margin: '0 auto' },
  
  formCard: {
    width: '100%', maxWidth: '420px', background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '30px', padding: '45px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', transition: 'all 0.5s ease',
  },
  headerSection: { marginBottom: '30px', textAlign: 'center' },
  iconCircle: { fontSize: '40px', marginBottom: '10px' },
  headerTitle: { color: '#fff', fontSize: '28px', fontWeight: '600', margin: 0 },
  headerSubtitle: { color: '#a0aec0', fontSize: '15px', marginTop: '5px' },
  
  inputGroup: { marginBottom: '20px' },
  label: { color: '#cbd5e0', fontSize: '13px', marginBottom: '8px', display: 'block', fontWeight: '500' },
  inputWrapper: (focused, error) => ({
    background: 'rgba(0, 0, 0, 0.25)', borderRadius: '12px',
    border: error ? '1px solid #e53e3e' : focused ? '1px solid #81e6d9' : '1px solid rgba(255, 255, 255, 0.05)',
    display: 'flex', alignItems: 'center', transition: 'all 0.3s ease',
  }),
  input: { width: '100%', padding: '16px', background: 'transparent', border: 'none', color: '#fff', fontSize: '14px', outline: 'none' },
  eyeBtn: { background: 'transparent', border: 'none', color: '#a0aec0', padding: '0 15px', cursor: 'pointer', fontSize: '16px' },
  
  submitBtn: (disabled) => ({
    width: '100%', padding: '16px', marginTop: '10px',
    background: disabled ? '#4a5568' : '#81e6d9',
    color: disabled ? '#a0aec0' : '#1a202c', border: 'none', borderRadius: '12px',
    fontSize: '14px', fontWeight: '700', cursor: disabled ? 'not-allowed' : 'pointer',
    letterSpacing: '1px', boxShadow: disabled ? 'none' : '0 4px 14px 0 rgba(129, 230, 217, 0.39)',
  }),
  
  resendBtn: (disabled) => ({
    background: 'transparent', border: 'none', color: disabled ? '#718096' : '#81e6d9',
    fontSize: '12px', fontWeight: '600', cursor: disabled ? 'default' : 'pointer',
    textDecoration: disabled ? 'none' : 'underline', padding: 0
  }),
  
  footerLink: { textAlign: 'center', marginTop: '25px', fontSize: '13px' },
  errorBox: { background: 'rgba(229, 62, 62, 0.2)', color: '#feb2b2', padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '15px' },
  successBox: { background: 'rgba(72, 187, 120, 0.2)', color: '#9ae6b4', padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '15px' },
  fieldError: { color: '#fc8181', fontSize: '12px', marginTop: '6px' },
};